import React, { type FC } from 'react';
import classNames from 'classnames';
import { NavLink, Outlet } from 'react-router-dom';
import { ReactComponent as Logo } from '../assets/logo.svg';
import { ReactComponent as DashboardIcon } from '../assets/ic_dashboard.svg';
import { ReactComponent as DriversIcon } from '../assets/ic_drivers.svg';
import { ReactComponent as TripsIcon } from '../assets/ic_trips.svg';
import { ReactComponent as AlertsIcon } from '../assets/ic_alerts.svg';
import { ReactComponent as AppCustomisationIcon } from '../assets/ic_app_customisation.svg';
import { ReactComponent as SettingsIcon } from '../assets/ic_settings.svg';
import { ReactComponent as StylingIcon } from '../assets/ic_styling.svg';
import { ReactComponent as ArrowLeftIcon } from '../assets/ic_arrow_left.svg';
import useTenantStore from '../stores/TenantStore';
import TenantSwitcher from '../components/TenantSwitcher';
import TenantQueryWrapper from '../components/TenantQueryWrapper';
import usePreload from '../hooks/usePreload';
import { useDemoMode } from '../contexts/DemoModeContext';

const App: FC = () => {
  const [menuCollapsed, setMenuCollapsed] = React.useState(false);
  const { setLoading, setTenantConfig, ...data } = useTenantStore();
  const { canUseDemoMode, isDemoModeActive, setDemoModeActive } = useDemoMode();
  usePreload();

  const Link: FC<{
    to: string;
    title: string;
    Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    iconClassName: string;
    subMenu?: boolean;
  }> = ({ to, title, Icon, iconClassName, subMenu }) => (
    <div
      className={`flex flex-col items-stretch ${menuCollapsed ? 'px-6' : 'px-12'} max-md:px-6 mb-2 ${subMenu ? (menuCollapsed ? 'ml-4' : 'ml-8 max-md:ml-4') : ''}`}
    >
      <NavLink to={to}>
        {({ isActive }) => {
          return (
            <div
              className={`flex flex-row items-center py-4 transition-all hover:bg-white hover:rounded-[1.25rem] hover:pl-8 ${isActive ? 'bg-white rounded-[1.25rem] pl-8' : ''}`}
            >
              <Icon
                className={classNames('w-12 aspect-square mr-6', iconClassName)}
              />
              <span
                className={`text-xl text-marengo transition-all ${menuCollapsed ? 'hidden' : ''} max-md:hidden`}
              >
                {title}
              </span>
            </div>
          );
        }}
      </NavLink>
    </div>
  );

  return data.loading ? (
    <div></div>
  ) : (
    <TenantQueryWrapper>
      <div className="h-screen flex flex-col">
        <div
          className="fixed h-header-height px-14 py-6 top-0 left-0 right-0 z-10"
          style={{
            background: `linear-gradient(90deg, #000 0%, ${
              data.appColors
                ?.find((c) => c.type === 'P400')
                ?.value?.toLowerCase() ?? '#fff'
            } 100%)`,
          }}
        >
          <div className="flex justify-between items-center">
            <div className="flex flex-row items-center">
              <Logo className="object-contain w-auto h-[7rem] mr-[1rem]" />
            </div>

            <div className="flex items-center gap-4">
              {canUseDemoMode && (
                <button
                  onClick={() => {
                    setDemoModeActive(!isDemoModeActive);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    isDemoModeActive
                      ? 'bg-amber-500 text-white hover:bg-amber-600'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  title={
                    isDemoModeActive ? 'Disable Demo Mode' : 'Enable Demo Mode'
                  }
                >
                  {isDemoModeActive ? '🔒 Demo Mode ON' : '👁️ Demo Mode OFF'}
                </button>
              )}
              <TenantSwitcher />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <nav
          className={`fixed left-0 top-header-height bottom-0 overflow-y-auto py-12 bg-whiteSmoke z-10 transition-all ${menuCollapsed ? 'w-collapsed-sidebar-width' : 'w-sidebar-width'} max-md:w-collapsed-sidebar-width`}
        >
          <div
            className={classNames(
              'flex justify-center items-center py-6 mb-10',
              !menuCollapsed && 'px-4 mx-12 rounded-2xl bg-white',
              'max-md:px-0 max-md:mx-0 max-md:bg-inherit',
            )}
          >
            <img
              src={data?.appAssets?.find((a) => a.name === 'Logo')?.url ?? ''}
              alt="logo"
              className={`object-contain transition-all ${menuCollapsed ? 'w-24' : 'max-w-72'} h-auto max-md:w-24`}
            />
          </div>

          <Link
            to="app-customisation"
            title="App Customisation"
            Icon={AppCustomisationIcon}
            iconClassName="fill-stormGray"
          />
          <Link
            to="app-customisation/features"
            title="Features"
            Icon={SettingsIcon}
            iconClassName="h-9 fill-stormGray"
            subMenu
          />
          <Link
            to="app-customisation/look-and-feel"
            title="Style"
            Icon={StylingIcon}
            iconClassName="h-10 fill-stormGray"
            subMenu
          />

          <div
            className={`shrink-0 h-px flex-1 ${menuCollapsed ? 'mx-4' : 'mx-12'} max-md:mx-4 my-10 border-b border-solid bg-zinc-300 border-zinc-300 max-md:max-w-full`}
          />

          <Link
            to="dashboard"
            title="Dashboard"
            Icon={DashboardIcon}
            iconClassName="stroke-stormGray"
          />
          <Link
            to="drivers"
            title="Users"
            Icon={DriversIcon}
            iconClassName="fill-stormGray"
          />
          <Link
            to="trips"
            title="Trips"
            Icon={TripsIcon}
            iconClassName="stroke-stormGray"
          />
          <Link
            to="device-status-summary"
            title="Device Status Summary"
            Icon={AlertsIcon}
            iconClassName="stroke-stormGray"
          />

          <div
            className={`shrink-0 h-px flex-1 ${menuCollapsed ? 'mx-4' : 'mx-12'} max-md:mx-4 my-10 border-b border-solid bg-zinc-300 border-zinc-300 max-md:max-w-full`}
          />
        </nav>

        {/* Sidebar toggle button */}
        <button
          className={`fixed flex w-16 ml-[-1.5rem] mt-12 aspect-square bg-ghostWhite rounded-xl justify-center items-center z-10 transform transition-all ${menuCollapsed ? 'scale-x-[-1] left-collapsed-sidebar-width' : 'left-sidebar-width'} max-md:hidden`}
          onClick={() => {
            setMenuCollapsed((prev) => !prev);
          }}
        >
          <ArrowLeftIcon className="stroke-cloudCover w-10" />
        </button>

        {/* Main content area */}
        <div
          className={`min-h-screen pt-header-height p-m24 transition-all ${menuCollapsed ? 'ml-collapsed-sidebar-width' : 'ml-sidebar-width'} max-md:ml-collapsed-sidebar-width`}
        >
          {isDemoModeActive && (
            <div className="bg-amber-500 text-white px-4 py-2 rounded-lg mb-4 flex items-center justify-between">
              <span className="font-medium">
                🔒 Demo Mode Active - Personal data is masked for privacy
              </span>
              <button
                onClick={() => {
                  setDemoModeActive(false);
                }}
                className="text-sm underline hover:no-underline"
              >
                Disable
              </button>
            </div>
          )}
          <Outlet />
        </div>
      </div>
    </TenantQueryWrapper>
  );
};

export default App;
